/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, ArrowUpDown } from 'lucide-react'
import type { UserProfile } from '@/types/auth.types'
import type { Role } from '@/api/users/roles.api'
import type { UserSortField, SortOrder } from '@/api/users/users.api'

interface UserTableProps {
  users: UserProfile[]
  roles?: Role[]
  onEdit: (user: UserProfile) => void
  onDelete: (id: string) => void
  sortBy?: UserSortField
  sortOrder?: SortOrder
  onSort: (field: UserSortField) => void
}

export function UserTable({
  users,
  roles,
  onEdit,
  onDelete,
  sortBy,
  sortOrder,
  onSort,
}: UserTableProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const getRoleNames = (roleIds: string[]) => {
    if (!roles || roleIds.length === 0) return '-'
    const roleNames = roleIds
      .map((id) => roles.find((r) => r.id === id)?.name)
      .filter(Boolean)
    return roleNames.length > 0 ? roleNames.join(', ') : '-'
  }

  const handleDeleteClick = (id: string) => {
    setDeletingId(id)
    onDelete(id)
    // Reset deletingId po krátkém timeoutu
    setTimeout(() => setDeletingId(null), 1000)
  }

  const getSortIcon = (field: UserSortField) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4" />
    return <ArrowUpDown className={`h-4 w-4 ${sortOrder === 'DESC' ? 'rotate-180' : ''}`} />
  }

  const SortableHeader = ({
    field,
    children,
  }: {
    field: UserSortField
    children: React.ReactNode
  }) => (
    <TableHead>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 data-[state=open]:bg-accent"
        onClick={() => onSort(field)}
      >
        {children}
        {getSortIcon(field)}
      </Button>
    </TableHead>
  )

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center text-muted-foreground">
        Žádní uživatelé nenalezeni
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader field="email">Email</SortableHeader>
            <SortableHeader field="firstName">Jméno</SortableHeader>
            <SortableHeader field="lastName">Příjmení</SortableHeader>
            <TableHead>Telefon</TableHead>
            <TableHead>Role</TableHead>
            <SortableHeader field="isActive">Stav</SortableHeader>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow
              key={user.id}
              onClick={() => onEdit(user)}
              className="cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.firstName}</TableCell>
              <TableCell>{user.lastName}</TableCell>
              <TableCell className="text-muted-foreground">{user.phone || '-'}</TableCell>
              <TableCell className="text-muted-foreground">{getRoleNames(user.roles)}</TableCell>
              <TableCell>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                    user.isActive
                      ? 'bg-green-50 text-green-700'
                      : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  {user.isActive ? 'Aktivní' : 'Neaktivní'}
                </span>
              </TableCell>
              <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={deletingId === user.id}
                    >
                      <span className="sr-only">Otevřít menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(user)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Upravit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault()
                        handleDeleteClick(user.id)
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Smazat
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
